import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  Badge, 
  Button,
  createStatusBadge,
  createTagBadge
} from '@h2/ui';
import { ChevronDown, ChevronRight, X } from 'lucide-react';

interface OrderDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrders: any[];
  onRemoveOrder?: (orderId: string) => void;
  onAcceptOrders?: () => void;
}

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ 
  isOpen, 
  onClose, 
  selectedOrders,
  onRemoveOrder,
  onAcceptOrders
}) => {
  // 3개 이하인 경우 모두 확장된 상태로 초기화
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(() => {
    if (selectedOrders.length <= 3) {
      return new Set(selectedOrders.map(order => order.id));
    }
    return new Set();
  });

  // selectedOrders가 변경될 때마다 확장 상태 업데이트
  React.useEffect(() => {
    console.log('OrderDetailModal received selectedOrders:', selectedOrders?.length);
    selectedOrders.forEach(order => {
      console.log(`Order ${order.id} has ${order.jobs?.length || 0} jobs`);
    });
    
    if (selectedOrders.length <= 3) {
      setExpandedOrders(new Set(selectedOrders.map(order => order.id)));
    } else {
      setExpandedOrders(new Set());
    }
  }, [selectedOrders]);

  const toggleOrderExpanded = (orderId: string) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const formatDuration = (duration: string) => {
    // HH:MM:SS 형태를 더 읽기 쉽게 포맷
    const parts = duration.split(':');
    if (parts.length === 3) {
      const hours = parseInt(parts[0]);
      const minutes = parseInt(parts[1]);
      const seconds = parseInt(parts[2]);
      
      if (hours > 0) {
        return `${hours}시간 ${minutes}분 ${seconds}초`;
      } else {
        return `${minutes}분 ${seconds}초`;
      }
    }
    return duration;
  };

  // 등록 가능 여부 계산 (모든 선택된 주문이 '등록됨' 상태이고 같은 언어인지 확인)
  const canAcceptOrders = React.useMemo(() => {
    if (selectedOrders.length === 0) return false;
    
    // 모든 주문이 '등록됨' 상태인지 확인
    const allRegistered = selectedOrders.every(order => order.status === '등록됨');
    if (!allRegistered) return false;
    
    // 모든 주문의 원본언어가 동일한지 확인
    const allJobs = selectedOrders.flatMap(order => order.jobs);
    if (allJobs.length === 0) return false;
    
    const firstJobSourceLang = allJobs[0]?.sourceLanguage;
    
    const allSameSourceLanguage = allJobs.every(job => 
      job.sourceLanguage === firstJobSourceLang
    );
    
    return allSameSourceLanguage;
  }, [selectedOrders]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[90vh] w-[95vw] flex flex-col">
        <DialogHeader className="pb-4 flex-shrink-0">
          <DialogTitle className="text-xl font-semibold">
            선택된 주문 상세정보 ({selectedOrders.length}개)
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 min-h-0">
          <div className="space-y-4">
            {selectedOrders.map((order) => (
              <div key={order.id} className="border rounded-lg">
                {/* 주문 헤더 */}
                <div className="p-4 flex items-center justify-between">
                  <div 
                    className="flex items-center gap-4 flex-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded p-2 -m-2"
                    onClick={() => toggleOrderExpanded(order.id)}
                  >
                    <div className="flex items-center gap-2">
                      {expandedOrders.has(order.id) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      <span className="font-medium">{order.id}</span>
                    </div>
                    
                    <div className="flex gap-1">
                      {order.tags.map((tag: string) => (
                        <Badge
                          key={tag}
                          {...createTagBadge(tag)}
                          size="xs"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    
                    <div className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-md">
                      {order.title}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm flex-shrink-0">
                    <Badge {...createStatusBadge(order.status)} size="xs">
                      {order.status}
                    </Badge>
                    <span className="text-gray-500">
                      {order.jobs?.length || 0}개 작업
                    </span>
                    {onRemoveOrder && selectedOrders.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveOrder(order.id);
                        }}
                        className="h-6 w-6 p-0 ml-2 hover:bg-red-100 hover:text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* 확장된 내용 */}
                {expandedOrders.has(order.id) && (
                  <div className="border-t bg-gray-50/50 dark:bg-gray-800/25">
                    {/* 주문 기본 정보 */}
                    <div className="p-4 border-b">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-900 dark:text-gray-100">고객</span>
                          <div className="text-gray-600 dark:text-gray-400">
                            <div>{order.customer.name}</div>
                            <div className="text-xs">{order.customer.email}</div>
                          </div>
                        </div>
                        <div>
                          <span className="font-medium text-gray-900 dark:text-gray-100">등록일자</span>
                          <div className="text-gray-600 dark:text-gray-400">{order.registeredAt}</div>
                        </div>
                        <div>
                          <span className="font-medium text-gray-900 dark:text-gray-100">결제상태</span>
                          <div>
                            <Badge {...createStatusBadge(order.paymentStatus)} size="xs">
                              {order.paymentStatus}
                            </Badge>
                          </div>
                        </div>
                        <div>
                          <span className="font-medium text-gray-900 dark:text-gray-100">금액</span>
                          <div className="text-gray-600 dark:text-gray-400">
                            {order.totalAmount.toLocaleString()}
                            <span className="text-xs"> ({order.paidAmount.toLocaleString()})</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* JamakeJob 목록 */}
                    <div className="p-4">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                        작업 목록 ({order.jobs?.length || 0}개)
                      </h4>
                      
                      {order.jobs && order.jobs.length > 0 ? (
                        <div className="space-y-2">
                          {order.jobs.map((job: any) => (
                            <div 
                              key={job.id} 
                              className="bg-white dark:bg-gray-800 border rounded p-3"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                    {job.name}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {job.sourceLanguage} → {job.targetLanguage} | {job.jobType} | {job.jobScope}
                                  </div>
                                  {job.requirements && (
                                    <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                                      📝 {job.requirements}
                                    </div>
                                  )}
                                  {job.glossary && (
                                    <div className="text-xs text-gray-600 dark:text-gray-300">
                                      📚 {job.glossary}
                                    </div>
                                  )}
                                </div>
                                <div className="text-right text-xs text-gray-500 dark:text-gray-400">
                                  <div>{formatDuration(job.videoDuration)}</div>
                                  <div>{job.registeredAt}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                          등록된 작업이 없습니다.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 푸터 */}
        <div className="flex justify-between items-center gap-2 pt-4 border-t flex-shrink-0 bg-white dark:bg-gray-900">
          <div>
            {selectedOrders.length > 0 && (
              <Button
                variant="default"
                onClick={onAcceptOrders}
                disabled={!canAcceptOrders}
                className={!canAcceptOrders ? 'opacity-30 cursor-not-allowed' : ''}
              >
                등록
              </Button>
            )}
          </div>
          <Button variant="outline" onClick={onClose}>
            닫기
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDetailModal;
